import type { OnInit } from '@angular/core';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, HostListener, inject } from '@angular/core';
import type { BsModalRef } from 'ngx-bootstrap/modal';
import { BsModalService } from 'ngx-bootstrap/modal';
import type { Observable } from 'rxjs';
import { combineLatest, delay } from 'rxjs';
import { filter, map, take, tap, withLatestFrom } from 'rxjs/operators';
import { Actions, ofType } from '@ngrx/effects';

import type { Uuid } from '@common/types';
import type { SiteEntity, SiteListEntity } from '@common/types/site.type';
import type { SitePageEntity } from '@common/types/site-page.type';
import type { SiteContentEntity } from '@common/types/site-content.type';
import type { NPS } from '@ui/sp-nps/nps-loader.component';
import { EContentSources, type ELanguages, Widgets } from '@common/enums';
import { EDrawerType, ESelectedEntityType, PreviewMode } from '@common/types/layout.type';

import { type WebLegacyWidgetEntity } from '@web/types';

import { AmplitudeAnalyticsService } from '@common/services/amplitude-analytics.service';
import { ShopCartIconHttpService } from '@common/services/REST/shop-cart-icon.http.service';

import { environment } from '@web-env/environment';
import { EDU_FREE_TARIFF } from '@web/core/constants/tariff.const';
import { checkIsElementAvailable } from '@web/helpers/tariffs';
import { getWidgetName } from '@web/helpers/widgets-names.map';

import { ResetSiteVersionModalComponent } from '@web/shared/modals/reset-site-version-modal/reset-site-version-modal.component';
import { PaidElementsModalComponent } from '@web/shared/modals/paid-elements-modal/paid-elements-modal.component';
import { InvalidUserTariffModalComponent } from '@web/shared/modals/invalid-user-tariff-modal/invalid-user-tariff-modal.component';

import { CaptchaLimitsHttpService, type CaptchaLimitWidget } from '@web/core/services/REST/captcha-limits.http.service';

import { SiteService } from '@web/store/website/site/site.service';
import { ContentService } from '@web/store/website/content/content.service';
import { PagesService } from '@web/store/website/pages/pages.service';

import { AppFacade } from '@web/store/app/app.facade';
import { LayoutFacade } from '@web/store/layout/layout.facade';
import { RouterFacade } from '@web/store/router/router.facade';
import { SiteFacade } from '@web/store/website/site/site.facade';
import { PagesFacade } from '@web/store/website/pages/pages.facade';
import { ContentFacade } from '@web/store/website/content/content.facade';
import { UserFacade } from '@web/store/user/user.facade';
import { ModalsFacade } from '@web/store/modals/modals.facade';
import { HistoryFacade } from '@web/store/history/history.facade';
import { WidgetsFacade } from '@web/store/page-structure/widgets/widgets.facade';
import { SitesCollectionFacade } from '@web/store/collections/sites/sites.facade';
import { ShopCartIconFacade } from '@web/store/shop-cart-icon/shop-cart-icon.facade';
import { LogFacade } from '@web/store/log/log.facade';

import AppActions from '@web/store/app/app.actions';
import SiteActions from '@web/store/website/site/site.actions';
import PagesActions from '@web/store/website/pages/pages.actions';
import { SpBaseHeaderComponent } from '@common/layouts/sp-base-header/src/sp-base-header.component';
import { CommonModule } from '@angular/common';
import { SpBaseLogoComponent } from '@common/layouts/sp-base-logo/src/sp-base-logo.component';
import { SpPartnersCatalogComponent } from '@libs/components/sp-partners-catalog/src/sp-partners-catalog.component';
import { BsDropdownModule } from 'ngx-bootstrap/dropdown';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { SpNpsComponent } from '@libs/ui/sp-nps/nps-loader.component';
import { TranslateModule } from '@ngx-translate/core';
import { SpTimeFormat } from '@common/pipes/time-format.pipe';
import { UpgradePlanPopoverComponent } from '@web/shared/popovers/upgrade-plan/upgrade-plan-popover.component';
import { PaidFeaturesModalComponent } from '@web/shared/modals/paid-features-modal/paid-features-modal.component';

import { OnboardingWizardAbTestService } from '@web/core/services/onboarding-wizard-ab-test.service';
import { widgetNameOf } from '@common/helpers/widget-envelope';

@Component({
    selector: 'web-header',
    templateUrl: './header.component.html',
    styleUrls: ['./header.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CommonModule,
        SpBaseHeaderComponent,
        SpBaseLogoComponent,
        SpPartnersCatalogComponent,
        BsDropdownModule,
        TooltipModule,
        SpNpsComponent,
        TranslateModule,
        SpTimeFormat,
        UpgradePlanPopoverComponent,
    ],
})
export class HeaderComponent implements OnInit {
    private readonly actions$: Actions = inject(Actions);
    private readonly modalService: BsModalService = inject(BsModalService);
    private readonly appFacade: AppFacade = inject(AppFacade);
    private readonly layoutFacade: LayoutFacade = inject(LayoutFacade);
    private readonly siteFacade: SiteFacade = inject(SiteFacade);
    private readonly pagesFacade: PagesFacade = inject(PagesFacade);
    private readonly contentFacade: ContentFacade = inject(ContentFacade);
    private readonly userFacade: UserFacade = inject(UserFacade);
    private readonly routerFacade: RouterFacade = inject(RouterFacade);
    private readonly modalsFacade: ModalsFacade = inject(ModalsFacade);
    private readonly historyFacade: HistoryFacade = inject(HistoryFacade);
    private readonly widgetsFacade: WidgetsFacade = inject(WidgetsFacade);
    private readonly logFacade = inject(LogFacade);

    private readonly siteService: SiteService = inject(SiteService);
    private readonly contentService: ContentService = inject(ContentService);
    private readonly pagesService: PagesService = inject(PagesService);
    private readonly sitesCollectionFacade: SitesCollectionFacade = inject(SitesCollectionFacade);
    private readonly changeDetectorRef: ChangeDetectorRef = inject(ChangeDetectorRef);
    private readonly captchaLimitsHttpService: CaptchaLimitsHttpService = inject(CaptchaLimitsHttpService);
    private readonly shopCartIconFacade: ShopCartIconFacade = inject(ShopCartIconFacade);
    private readonly shopCartIconHttpService: ShopCartIconHttpService = inject(ShopCartIconHttpService);

    private readonly onboardingWizardAbTestService: OnboardingWizardAbTestService =
        inject(OnboardingWizardAbTestService);

    public readonly isCartSettingsChanged$: Observable<any> = this.shopCartIconFacade.isCartSettingsChanged$;

    public readonly locale$: Observable<ELanguages> = this.appFacade.locale$;
    public readonly isReady$: Observable<boolean> = this.appFacade.isReady$;
    public readonly isAdmin$: Observable<boolean> = this.appFacade.isAdmin$;
    public readonly isEcommerceMode$: Observable<boolean> = this.appFacade.isEcommerceMode$;
    public readonly dataLayer$: Observable<any[]> = this.appFacade.dataLayer$;

    public readonly site$: Observable<SiteEntity> = this.siteFacade.site$;
    public readonly isSaving$: Observable<boolean> = this.siteFacade.isSaving$;
    public readonly isNewSite$: Observable<boolean> = this.siteFacade.isNewSite$;
    public readonly availablePageCount$: Observable<number> = this.siteFacade.siteAvailablePageCount$;
    public readonly siteId$: Observable<number | Uuid> = this.siteFacade.id$;

    public readonly availableSiteCount$: Observable<number> = this.userFacade.userAvailableSiteCount$;

    public readonly isPublishing$: Observable<boolean> = this.pagesFacade.isPublishing$;
    public readonly isEduSite$: Observable<boolean> = this.pagesFacade.isEduSite$;
    public readonly currentPage$: Observable<SitePageEntity> = this.pagesFacade.currentPage$;
    public readonly isPersonalSite$: Observable<boolean> = this.pagesFacade.isPersonalSite$;

    public readonly isPreview$: Observable<boolean> = this.layoutFacade.isPreview$;
    public readonly isPreviewMobile$: Observable<boolean> = this.layoutFacade.isPreviewMobile$;
    public readonly isPreviewDesktop$: Observable<boolean> = this.layoutFacade.isPreviewDesktop$;
    public readonly isPreviewTablet$: Observable<boolean> = this.layoutFacade.isPreviewTablet$;

    public readonly isDirty$: Observable<boolean> = this.contentFacade.isDirty$;
    public readonly isAutoSavePaused$: Observable<boolean> = this.contentFacade.isAutoSavePaused$;
    public readonly isCurrentPageDeleted$: Observable<boolean> = combineLatest([
        this.routerFacade.routerParams$,
        this.contentFacade.deletedPageIds$,
    ]).pipe(map(([{ pageId }, deleted]) => deleted.includes(String(pageId))));

    public readonly introFinished$ = this.actions$.pipe(ofType(AppActions.introFinished), take(1));

    public readonly siteContent$: Observable<SiteContentEntity> = this.contentFacade.siteContent$;
    public readonly pageContent$: Observable<SiteContentEntity> = this.contentFacade.pageContent$;

    public readonly pageContentStructureUpdatedAt$: Observable<string | null> =
        this.contentFacade.pageContentStructureUpdatedAt$;

    public readonly isPrevEmpty$: Observable<boolean> = this.historyFacade.isPrevEmpty$;
    public readonly isNextEmpty$: Observable<boolean> = this.historyFacade.isNextEmpty$;
    public readonly isUserTariffFree$: Observable<boolean> = this.userFacade.isUserTariffFree$;

    public readonly sites$: Observable<SiteListEntity[]> = this.appFacade.isAdminCollectionWrapper$(
        this.sitesCollectionFacade.sites$
    );

    public readonly isShowPageDrawer$: Observable<boolean> = this.layoutFacade.showPageDrawer$;
    public readonly isShowNavigationDrawer$: Observable<boolean> = this.layoutFacade.showNavigationDrawer$;

    public readonly previewMode = PreviewMode;
    public readonly contentSource = EContentSources.Website;

    public readonly showOnboardingSteps$: Observable<boolean> = this.onboardingWizardAbTestService.showOnboardingSteps$;

    public siteServiceLink: string = environment.siteServiceLink;
    public showLabel: 'saved' | 'not_saved' | 'published' = null;
    public lastPublishedDate: string = '';

    private npsShown: boolean = false;
    private isMacOS: boolean = false;
    private nps: NPS;
    private sites: SiteListEntity[];

    public get onboardStatus(): boolean {
        return JSON.parse(localStorage.getItem('sp-web-pages-onboard')) as boolean;
    }

    public ngOnInit(): void {
        this.setPublishedDate();

        this.appFacade.isMacOS$.subscribe((isMacOS: boolean) => {
            this.isMacOS = isMacOS;
        });

        this.currentPage$.pipe(take(1)).subscribe((page) => {
            this.showLabel = this.isPageNew(page) ? 'not_saved' : null;
        });

        this.actions$
            .pipe(
                ofType(SiteActions.saveSiteSuccess),
                withLatestFrom(this.isAdmin$, this.sites$, (_, isAdmin, sites) => ({ isAdmin, sites })),
                take(1)
            )
            .subscribe(({ isAdmin, sites }) => {
                if (sites?.length !== 0) {
                    this.nps?.updateSessionNumber();
                }

                if (isAdmin) {
                    return;
                }

                this.sitesCollectionFacade.loadSitesCollection();
            });

        this.actions$
            .pipe(
                ofType(SiteActions.saveSiteSuccess),
                tap(() => {
                    this.showLabel = 'saved';
                }),
                delay(3000)
            )
            .subscribe(() => {
                if (this.showLabel === 'published') {
                    this.showLabel = 'published';
                } else {
                    this.showLabel = null;
                }
                this.changeDetectorRef.detectChanges();
            });

        // Shown until the retried save goes through; `saveSiteSuccess` above replaces it.
        this.actions$.pipe(ofType(SiteActions.saveSiteOffline)).subscribe(() => {
            this.showLabel = 'not_saved';
            this.changeDetectorRef.detectChanges();
        });

        this.actions$
            .pipe(
                ofType(PagesActions.activatePageSuccess),
                tap(() => {
                    this.showLabel = 'published';
                }),
                delay(5000)
            )
            .subscribe(() => {
                this.nps?.setPublishTime(new Date());

                if (this.nps?.getSessionNumber() === 1) {
                    this.nps?.updateSessionNumber();
                }
                this.changeDetectorRef.detectChanges();
            });
    }

    public showThemeSettings(): void {
        this.isEcommerceMode$.pipe(take(1)).subscribe((isEcommerceMode) => {
            if (isEcommerceMode) {
                return;
            }
            this.layoutFacade.setSelectedEntity({ entityType: ESelectedEntityType.theme });
        });
    }

    public changePreviewMode(previewMode: PreviewMode): void {
        this.layoutFacade.changePreviewMode(previewMode);
    }

    public showPreview(): void {
        //close pages drawer
        this.layoutFacade.showPageDrawer$.pipe(take(1)).subscribe((value) => {
            if (value) {
                this.layoutFacade.closeDrawer();
            }
        });

        //close navigation drawer
        this.layoutFacade.showNavigationDrawer$.pipe(take(1)).subscribe((value) => {
            if (value) {
                this.layoutFacade.closeDrawer();
            }
        });

        this.layoutFacade.showPreview();
    }

    public hidePreview(): void {
        this.layoutFacade.hidePreview();
    }

    public showSavedVersion(): void {
        const modal = this.modalService.show(ResetSiteVersionModalComponent);
        modal.content.onClose.subscribe((next) => {
            if (!next) {
                return;
            }

            combineLatest([this.contentFacade.siteContent$, this.contentFacade.pageContent$])
                .pipe(take(1))
                .subscribe(([site, page]) => {
                    this.contentFacade.setIsReset(true);
                    this.contentService.setPublishedContent(site, page);
                });
        });
    }

    private firstPublicationSent: boolean = false;
    public publish(): void {
        combineLatest([
            this.isAdmin$,
            this.isEduSite$,
            this.widgetsFacade.widgets$,
            this.userFacade.tariffAbbr$,
            this.routerFacade.routerParams$,
            this.shopCartIconFacade.cartIconData$,
            this.shopCartIconFacade.isCartEnabled$,
            this.isNewSite$,
            this.userFacade.eduUserTariff$,
            this.isAutoSavePaused$,
        ])
            .pipe(take(1))
            .subscribe(
                ([
                    isAdmin,
                    isEduSite,
                    widgets,
                    abbr,
                    { siteId, pageId },
                    cartIconData,
                    isCartEnabled,
                    isNewSite,
                    eduTariff,
                    isAutoSavePaused,
                ]) => {
                    if (isAutoSavePaused) {
                        return;
                    }

                    if (isNewSite && !this.firstPublicationSent) {
                        AmplitudeAnalyticsService.sendEvent({ event: 'sites_first_publication' });
                        this.firstPublicationSent = true;
                    } else {
                        AmplitudeAnalyticsService.sendEvent({ event: 'sites_another_publication' });
                    }

                    if (isCartEnabled) {
                        this.shopCartIconHttpService.setIconStyle(siteId, cartIconData, false).subscribe();
                        this.shopCartIconFacade.setChangedState(false);
                    }

                    if (isAdmin) {
                        return this.modalsFacade.showSaveModalHandler(false, true);
                    }

                    if (isEduSite) {
                        // Edu sites ignore site plan restrictions, except for the code widget:
                        // it stays paid-only and is unlocked by either a paid Courses plan or a paid site plan.
                        const isCodeWidgetRestricted =
                            eduTariff === EDU_FREE_TARIFF &&
                            widgets.some(({ name }) => name === Widgets.CODE) &&
                            !checkIsElementAvailable(Widgets.CODE, abbr).hasAccess;

                        if (isCodeWidgetRestricted) {
                            return this.modalService.show(PaidElementsModalComponent, {
                                initialState: { widgetNames: [getWidgetName(Widgets.CODE)] },
                            });
                        }

                        return this.modalsFacade.showSaveModalHandler(false, true);
                    }

                    if (!abbr) {
                        this.logFacade.sentInvalidUserTariffLog();
                        return this.modalService.show(InvalidUserTariffModalComponent);
                    }

                    const widgetNames = widgets.reduce((result, widget) => {
                        const name = widgetNameOf(widget);
                        const { hasAccess } = checkIsElementAvailable(name, abbr);

                        if (!hasAccess) {
                            result.push(getWidgetName(name));
                        }

                        return result;
                    }, []);

                    if (widgetNames.length) {
                        return this.modalService.show(PaidElementsModalComponent, { initialState: { widgetNames } });
                    }

                    this.updateCaptchaSettings(widgets, siteId, pageId);
                    this.modalsFacade.showSaveModalHandler(false, true);
                }
            );
    }

    public close(): void {
        let modalRef;
        if (!this.npsShown && this.nps) {
            modalRef = this.showNps();
        }

        if (!modalRef) {
            this.appFacade.exit();
            return;
        }

        modalRef.content.onClose$.subscribe(() => {
            this.appFacade.exit();
        });
    }

    public undo(): void {
        this.isPrevEmpty$
            .pipe(
                take(1),
                filter((isPrevEmpty: boolean) => !isPrevEmpty)
            )
            .subscribe(() => {
                this.historyFacade.undo();
            });
    }

    public redo(): void {
        this.isNextEmpty$
            .pipe(
                take(1),
                filter((isNextEmpty: boolean) => !isNextEmpty)
            )
            .subscribe(() => {
                this.historyFacade.redo();
            });
    }

    public showPageDrawer(isPreview): void {
        if (isPreview) {
            return;
        }

        this.layoutFacade.showPageDrawer$.pipe(take(1)).subscribe((value) => {
            if (value) {
                this.layoutFacade.closeDrawer();
                return;
            }

            this.layoutFacade.openDrawer({ drawerType: EDrawerType.page });
        });
    }

    public showNavigationDrawer(): void {
        this.layoutFacade.showNavigationDrawer$.pipe(take(1)).subscribe((value) => {
            if (value) {
                this.layoutFacade.closeDrawer();
                return;
            }

            this.layoutFacade.openDrawer({ drawerType: EDrawerType.navigation });
        });
    }

    @HostListener('document:keydown', ['$event'])
    public onKeydown(event: KeyboardEvent): void {
        if (event.code !== 'KeyZ') {
            return;
        }

        if (this.isMacOS) {
            if (event.shiftKey && event.metaKey && event.code === 'KeyZ' && !this.disabledHistoryActions) {
                event.preventDefault();
                this.redo();
                return;
            }

            if (event.metaKey && event.code === 'KeyZ' && !this.disabledHistoryActions) {
                event.preventDefault();
                this.undo();
                return;
            }
        }

        if (event.shiftKey && event.ctrlKey && event.code === 'KeyZ' && !this.disabledHistoryActions) {
            event.preventDefault();
            this.redo();
            return;
        }

        if (event.ctrlKey && event.code === 'KeyZ' && !this.disabledHistoryActions) {
            event.preventDefault();
            this.undo();
        }
    }

    public get undoTitle(): string {
        if (this.isMacOS) {
            return '⌘ + z';
        }

        return 'Ctrl+z';
    }

    public get redoTitle(): string {
        if (this.isMacOS) {
            return '⇧ + ⌘ + z';
        }

        return 'Ctrl+Shift+z';
    }

    public get isPublishButtonAvailable$() {
        return combineLatest([
            this.availableSiteCount$,
            this.currentPage$,
            this.site$,
            this.siteContent$,
            this.pageContent$,
            this.isCartSettingsChanged$,
            this.isDirty$,
        ]).pipe(
            map(([availableSiteCount, currentPage, site, siteContent, pageContent, isCartSettingsChanged, isDirty]) => {
                if (isCartSettingsChanged) {
                    return true;
                }

                if (site?.available_page_count <= 0) {
                    return false;
                }

                if (this.isLatestVersion(site, siteContent, pageContent)) {
                    return false;
                }

                if (isDirty) {
                    return false;
                }

                if (this.isPageActive(currentPage)) {
                    return true;
                }

                if (this.siteService.isSitePublished(site)) {
                    return site?.available_page_count > 0;
                }

                return availableSiteCount > 0;
            })
        );
    }

    public get isPublicationLimitReached$(): Observable<boolean> {
        return combineLatest([this.availableSiteCount$, this.site$, this.currentPage$]).pipe(
            map(([availableSiteCount, site, currentPage]) => {
                if (this.isPageActive(currentPage)) {
                    if (site?.available_page_count <= 0) {
                        return true;
                    }

                    return false;
                }

                if (this.isSitePublished(site)) {
                    return site?.available_page_count <= 0;
                }

                return availableSiteCount === 0;
            })
        );
    }

    public get disabledHistoryActions(): boolean {
        const tinymce = document.querySelectorAll('.tox.tox-tinymce.tox-tinymce-inline');
        const visibleTinyMce = Array.from(tinymce).filter((el: HTMLElement) => el.style.display !== 'none');
        return visibleTinyMce.length > 0;
    }

    public initNps(nps: NPS): void {
        this.sites$
            .pipe(
                filter((sites) => Boolean(sites.length)),
                take(1)
            )
            .subscribe((sites) => {
                this.sites = sites;
                this.nps = nps;

                const hasAnyPublishedSites = this.sites?.some((site) => this.siteService.isSitePublished(site));

                if (hasAnyPublishedSites) {
                    const earliestPublishedSite = this.sites.reduce((result, site) => {
                        if (!result?.pub_date) {
                            return site;
                        }

                        if (site.pub_date && result.pub_date) {
                            const resultPublishedAt = new Date(result.pub_date).getTime();
                            const sitePublishedAt = new Date(site.pub_date).getTime();

                            return resultPublishedAt < sitePublishedAt ? result : site;
                        }

                        return result;
                    }, null);
                    nps.setPublishTime(new Date(earliestPublishedSite.pub_date));
                }

                if (!hasAnyPublishedSites && this.sites.length > 0) {
                    nps.updateSessionNumber(1);
                }

                if (nps.getSessionNumber() === 1) {
                    this.initCursorLeftEvent();
                } else if (nps.getSessionNumber() === 2 && !this.npsShown) {
                    if (this.onboardStatus) {
                        this.nps?.show();
                        return;
                    }

                    this.introFinished$.subscribe(() => {
                        setTimeout(
                            () => {
                                this.showNps();
                            },
                            1000 * 60 * 5
                        );
                    });
                }
            });
    }

    private initCursorLeftEvent(): void {
        document.addEventListener(
            'mouseout',
            (e: MouseEvent) => {
                e = e ? e : (window.event as MouseEvent);
                const from = e.relatedTarget;
                if (!from) {
                    if (this.nps?.getSessionNumber() === 1 && this.sites?.length === 0 && !this.npsShown) {
                        this.showNps();
                    }
                }
            },
            false
        );
    }

    public showNps(): BsModalRef {
        return this.nps?.show((wasShown) => {
            this.npsShown = wasShown;
        });
    }

    public isSitePublished(site: SiteEntity): boolean {
        return this.siteService.isSitePublished(site);
    }

    public isPageNew(page: SitePageEntity): boolean {
        return this.pagesService.isPageNew(page);
    }

    public isPageNotActive(page: SitePageEntity): boolean {
        return this.pagesService.isPageNotActive(page);
    }

    public isPageActive(page: SitePageEntity): boolean {
        return this.pagesService.isPageActive(page);
    }

    public pageHasStructureChanges(pageContent: SiteContentEntity): boolean {
        if (!pageContent?.draft_structure) {
            return false;
        }
        return pageContent.structure !== pageContent.draft_structure;
    }

    public siteHasThemeChanges(siteContent: SiteContentEntity): boolean {
        if (!siteContent?.draft_theme_settings) {
            return false;
        }

        return siteContent.theme_settings !== siteContent.draft_theme_settings;
    }

    public siteHasSharedBlockChanges(siteContent: SiteContentEntity): boolean {
        if (!siteContent?.draft_shared_blocks) {
            return false;
        }

        const sharedBlocks =
            typeof siteContent.shared_blocks === 'string'
                ? siteContent.shared_blocks
                : JSON.stringify(siteContent.shared_blocks);
        const draftSharedBlocks =
            typeof siteContent.draft_shared_blocks === 'string'
                ? siteContent.draft_shared_blocks
                : JSON.stringify(siteContent.draft_shared_blocks);

        return sharedBlocks !== draftSharedBlocks;
    }

    public isDraft(site: SiteEntity, siteContent: SiteContentEntity, pageContent: SiteContentEntity): boolean {
        if (
            !this.isSitePublished(site) &&
            (!siteContent?.draft_theme_settings || !siteContent.draft_shared_blocks || !pageContent?.draft_structure)
        ) {
            return true;
        }

        return (
            this.siteHasThemeChanges(siteContent) ||
            this.pageHasStructureChanges(pageContent) ||
            this.siteHasSharedBlockChanges(siteContent)
        );
    }

    public isHavingAnyUnpublishedChanges(
        site: SiteEntity,
        page: SitePageEntity,
        siteContent: SiteContentEntity,
        pageContent: SiteContentEntity
    ): boolean {
        if (!this.isSitePublished(site) || !this.isPageActive(page)) {
            return false;
        }

        return (
            this.siteHasThemeChanges(siteContent) ||
            this.pageHasStructureChanges(pageContent) ||
            this.siteHasSharedBlockChanges(siteContent)
        );
    }

    public isLatestVersion(site: SiteEntity, siteContent: SiteContentEntity, pageContent: SiteContentEntity): boolean {
        if (!this.isSitePublished(site)) {
            return false;
        }

        return (
            !this.siteHasThemeChanges(siteContent) &&
            !this.pageHasStructureChanges(pageContent) &&
            !this.siteHasSharedBlockChanges(siteContent)
        );
    }

    public showPaidFeaturesModal(): void {
        this.modalService.show(PaidFeaturesModalComponent, { class: 'modal-lg' });
    }

    private setPublishedDate(): void {
        this.pageContentStructureUpdatedAt$.pipe(filter((date) => !!date)).subscribe((date) => {
            this.lastPublishedDate = date?.slice(0, 19).replace('T', ' ');
            this.changeDetectorRef.detectChanges();
        });
    }

    private updateCaptchaSettings(widgets: WebLegacyWidgetEntity[], siteId: string, pageId: string) {
        const widgetsWithCaptchaSettings = widgets.filter(
            (widget) =>
                widgetNameOf(widget) === Widgets.FORM ||
                widgetNameOf(widget) === Widgets.PAYMENT_FORM ||
                widgetNameOf(widget) === Widgets.LOGIN_EDU
        );

        if (!widgetsWithCaptchaSettings.length) {
            return;
        }

        const updateLimitWidgets: CaptchaLimitWidget[] = widgetsWithCaptchaSettings
            .filter((widget) => (widget.data as any)?.additionalFormSettings?.isEnabledCaptcha)
            .map((widget) => {
                const { id, data } = widget;
                const { additionalFormSettings } = data as any;
                const { captchaLimit } = additionalFormSettings;

                return { id, attempts: captchaLimit, minutes: 10 };
            });

        const deleteLimitWidgets: { id: string; attempts: number }[] = widgetsWithCaptchaSettings
            .filter((widget) => !(widget.data as any)?.additionalFormSettings?.isEnabledCaptcha)
            .map((widget) => {
                return { id: widget.id, attempts: 0 };
            });

        if (updateLimitWidgets.length) {
            this.captchaLimitsHttpService
                .setCaptchaLimits(siteId, pageId, updateLimitWidgets)
                .pipe(take(1))
                .subscribe(() => {});
        }

        if (deleteLimitWidgets.length) {
            this.captchaLimitsHttpService
                .deleteCaptchaLimits(siteId, pageId, deleteLimitWidgets)
                .pipe(take(1))
                .subscribe(() => {});
        }
    }
}
